// src/pages/ProductDetailPage.js
import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { products } from "../data/products";
import { useCart } from "../context/CartContext";
import "./ProductDetailPage.css";

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem, toggleWishlist, wishlist } = useCart();
  const product = products.find(p => p.id === Number(id));

  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedColorIdx, setSelectedColorIdx] = useState(0);
  const [added, setAdded] = useState(false);
  const [sizeError, setSizeError] = useState(false);
  const [activeTab, setActiveTab] = useState("details");

  if (!product) return (
    <div className="pdp-notfound">
      <h2>Product not found</h2>
      <button onClick={() => navigate("/")}>← Back to Shop</button>
    </div>
  );

  const isWishlisted = wishlist.some(w => w.id === product.id);
  const discount = Math.round(((product.mrp - product.price) / product.mrp) * 100);

  const handleAdd = () => {
    if (!selectedSize) { setSizeError(true); setTimeout(() => setSizeError(false), 1800); return; }
    addItem({ ...product, selectedSize, selectedColor: product.colors[selectedColorIdx] });
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const related = products.filter(p => p.category === product.category && p.id !== product.id).slice(0, 4);

  return (
    <div className="pdp">
      <div className="container">
        {/* Breadcrumb */}
        <div className="pdp__breadcrumb">
          <span onClick={() => navigate("/")} className="pdp__breadcrumb-link">Men</span>
          <span> / </span>
          <span onClick={() => navigate("/")} className="pdp__breadcrumb-link">{product.category}</span>
          <span> / </span>
          <span>{product.name}</span>
        </div>

        <div className="pdp__layout">
          {/* Image column */}
          <div className="pdp__images">
            <div className="pdp__main-img-wrap">
              <img src={product.img} alt={product.name} className="pdp__main-img" />
              {product.badge && <span className="pdp__badge">{product.badge}</span>}
              {discount > 0 && <span className="pdp__discount-tag">{discount}% OFF</span>}
            </div>
            {/* Thumbnail strip */}
            <div className="pdp__thumbs">
              {[0,1,2].map(i => (
                <div key={i} className={`pdp__thumb ${i === 0 ? "pdp__thumb--active" : ""}`}>
                  <img src={product.img} alt="" />
                </div>
              ))}
            </div>
          </div>

          {/* Info column */}
          <div className="pdp__info">
            <p className="pdp__brand">{product.brand}</p>
            <h1 className="pdp__name">{product.name}</h1>

            {/* Rating */}
            <div className="pdp__rating">
              <div className="pdp__stars">
                {[1,2,3,4,5].map(i => (
                  <svg key={i} width="14" height="14" viewBox="0 0 12 12">
                    <path d="M6 1l1.4 2.8L10.6 4l-2.3 2.2.5 3.3L6 8l-2.8 1.5.5-3.3L1.4 4l3.2-.2L6 1z"
                      fill={i <= Math.round(product.rating) ? "#f5a742" : "#e0e0e0"}/>
                  </svg>
                ))}
              </div>
              <span className="pdp__rating-num">{product.rating}</span>
              <span className="pdp__reviews">({product.reviews.toLocaleString()} reviews)</span>
            </div>

            {/* Price */}
            <div className="pdp__price-row">
              <span className="pdp__price">₹ {product.price.toLocaleString()}</span>
              {product.mrp > product.price && <span className="pdp__mrp">₹ {product.mrp.toLocaleString()}</span>}
              {discount > 0 && <span className="pdp__save-badge">{discount}% OFF</span>}
            </div>
            <p className="pdp__taxes-note">MRP incl. of all taxes</p>
            <p className="pdp__member-savings">Member Savings ₹ {Math.round((product.mrp - product.price) * 0.1)}</p>

            <div className="pdp__divider" />

            {/* Colors */}
            <div className="pdp__section">
              <h3 className="pdp__section-title">
                COLOR: <span style={{ fontWeight: 400, color: "var(--tss-gray-600)" }}>
                  {["Black","White","Red","Blue","Green","Brown","Navy","Dark"][selectedColorIdx] || "Color " + (selectedColorIdx + 1)}
                </span>
              </h3>
              <div className="pdp__colors">
                {product.colors.map((c, i) => (
                  <button key={i}
                    className={`pdp__color-btn ${selectedColorIdx === i ? "pdp__color-btn--active" : ""}`}
                    style={{ background: c, border: c === "#fff" ? "1px solid #ddd" : "none" }}
                    onClick={() => setSelectedColorIdx(i)}
                  />
                ))}
              </div>
            </div>

            {/* Sizes */}
            <div className="pdp__section">
              <div className="pdp__size-header">
                <h3 className="pdp__section-title">SELECT SIZE</h3>
                <button className="pdp__size-guide">Size Guide</button>
              </div>
              <div className={`pdp__sizes ${sizeError ? "pdp__sizes--error" : ""}`}>
                {product.sizes.map(s => (
                  <button key={s}
                    className={`pdp__size-btn ${selectedSize === s ? "pdp__size-btn--active" : ""}`}
                    onClick={() => { setSelectedSize(s); setSizeError(false); }}>
                    {s}
                  </button>
                ))}
              </div>
              {sizeError && <p className="pdp__size-error">Please select a size to continue</p>}
            </div>

            {/* CTA buttons */}
            <div className="pdp__ctas">
              <button className={`pdp__add-btn ${added ? "pdp__add-btn--added" : ""}`} onClick={handleAdd}>
                {added ? "✓ ADDED TO BAG" : "ADD TO BAG"}
              </button>
              <button className="pdp__wishlist-btn" onClick={() => toggleWishlist(product)}>
                <svg width="20" height="20" viewBox="0 0 24 24"
                  fill={isWishlisted ? "var(--tss-red)" : "none"}
                  stroke={isWishlisted ? "var(--tss-red)" : "currentColor"} strokeWidth="2">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                </svg>
                {isWishlisted ? "WISHLISTED" : "WISHLIST"}
              </button>
            </div>

            {/* Delivery check */}
            <div className="pdp__delivery">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>
              </svg>
              <span>Check delivery & services</span>
              <div className="pdp__delivery-input-row">
                <input className="pdp__pincode-input" placeholder="Enter pincode" maxLength={6} />
                <button className="pdp__check-btn">CHECK</button>
              </div>
            </div>

            {/* Trust row */}
            <div className="pdp__trust-row">
              {[
                { icon: "🚚", label: "Free Delivery" },
                { icon: "🔄", label: "30 Day Exchange" },
                { icon: "🔒", label: "Secure Payment" },
                { icon: "✅", label: "100% Authentic" },
              ].map(t => (
                <div key={t.label} className="pdp__trust-item">
                  <span>{t.icon}</span>
                  <span>{t.label}</span>
                </div>
              ))}
            </div>

            {/* Tabs */}
            <div className="pdp__tabs">
              {["details", "description", "reviews"].map(tab => (
                <button key={tab}
                  className={`pdp__tab ${activeTab === tab ? "pdp__tab--active" : ""}`}
                  onClick={() => setActiveTab(tab)}>
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>
            <div className="pdp__tab-content">
              {activeTab === "details" && (
                <table className="pdp__details-table">
                  <tbody>
                    <tr><td>Fit</td><td>{product.fit}</td></tr>
                    <tr><td>Fabric</td><td>{product.fabric}</td></tr>
                    <tr><td>Category</td><td>{product.category}</td></tr>
                    <tr><td>Available Sizes</td><td>{product.sizes.join(", ")}</td></tr>
                    <tr><td>Tags</td><td>{product.tags.join(", ")}</td></tr>
                  </tbody>
                </table>
              )}
              {activeTab === "description" && (
                <p className="pdp__desc">{product.desc}</p>
              )}
              {activeTab === "reviews" && (
                <div className="pdp__reviews">
                  <div className="pdp__rating-summary">
                    <div className="pdp__rating-big">{product.rating}</div>
                    <div>
                      <div className="pdp__stars">
                        {[1,2,3,4,5].map(i => (
                          <svg key={i} width="18" height="18" viewBox="0 0 12 12">
                            <path d="M6 1l1.4 2.8L10.6 4l-2.3 2.2.5 3.3L6 8l-2.8 1.5.5-3.3L1.4 4l3.2-.2L6 1z"
                              fill={i <= Math.round(product.rating) ? "#f5a742" : "#e0e0e0"}/>
                          </svg>
                        ))}
                      </div>
                      <p style={{ fontSize: 12, color: "var(--tss-gray-400)", marginTop: 4 }}>
                        Based on {product.reviews.toLocaleString()} reviews
                      </p>
                    </div>
                  </div>
                  {["Great quality, fits perfectly!", "Love the print, very accurate colors.", "Comfortable and durable. Will buy again."].map((r, i) => (
                    <div key={i} className="pdp__review-item">
                      <div className="pdp__review-stars">
                        {[1,2,3,4,5].map(s => <span key={s} style={{ color: s <= 5 - i ? "#f5a742" : "#e0e0e0" }}>★</span>)}
                      </div>
                      <p className="pdp__review-text">{r}</p>
                      <p className="pdp__review-meta">Verified Purchase · {i + 1} week{i > 0 ? "s" : ""} ago</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Related products */}
        {related.length > 0 && (
          <div className="pdp__related">
            <h2 className="pdp__related-title">YOU MAY ALSO LIKE</h2>
            <div className="pdp__related-grid">
              {related.map(p => (
                <div key={p.id} className="pdp__related-card" onClick={() => { navigate(`/product/${p.id}`); window.scrollTo(0,0); }}>
                  <img src={p.img} alt={p.name} />
                  <p className="pdp__related-name">{p.name}</p>
                  <p className="pdp__related-price">₹ {p.price.toLocaleString()}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
